import { type RefObject, useContext } from 'react';

import { useMiniGameRollDiceMutation } from '@/api/queries/game';
import {
  type ActiveTileRef,
  type MiniGameDiceRollResponseType,
  type MiniGameType,
} from '@/common/types/dice-game';
import { HeaderAnimationContext } from '@/context/HeaderAnimationContext';
import { setTabBarHighlight } from '@/redux/reducers/appReducer';
import {
  setActiveBoard,
  setMiniGameDiceResult,
} from '@/redux/reducers/diceGameReducer';
import { addToBalance } from '@/redux/reducers/userReducer';
import { selectMiniGameReward } from '@/redux/selectors/diceGameSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { DiceGameContext } from '../context';

interface UseMiniGameReturnType {
  boardPosition?: number;
  diceCount?: number;
  boardKind?: MiniGameType;
  rollDiceMutation: ReturnType<typeof useMiniGameRollDiceMutation>;
  responseHandler: (response: MiniGameDiceRollResponseType) => Promise<void>;
  reward?: number;
}

interface UseMiniDiceGameArgsType {
  activeTileRef: RefObject<ActiveTileRef>;
  tileAnimationSteps: number[][] | null;
}

const useMiniDiceGame = ({
  activeTileRef,
  tileAnimationSteps,
}: UseMiniDiceGameArgsType): UseMiniGameReturnType => {
  const diceCount = useAppSelector(
    (state) => state.diceGame.miniGame?.movesLeft,
  );
  const boardPosition = useAppSelector(
    (state) => state.diceGame.miniGame?.position,
  );
  const boardKind = useAppSelector((state) => state.diceGame.miniGame?.type);
  const mainGameBoardPositon = useAppSelector(
    (state) => state.diceGame.user?.position,
  );
  const reward = useAppSelector(selectMiniGameReward);
  const skipAnimation = useAppSelector((state) => state.diceGame.skipAnimation);
  const rollDiceMutation = useMiniGameRollDiceMutation();
  const dispatch = useAppDispatch();
  const { setFlyingAnimation, setIsRewardScaled } = useContext(DiceGameContext);
  const { setTgemBalanceScale } = useContext(HeaderAnimationContext);

  const responseHandler = async (
    response: MiniGameDiceRollResponseType,
  ): Promise<void> => {
    const moveTile = (): void => {
      if (mainGameBoardPositon !== undefined) {
        const [x, y] = tileAnimationSteps
          ? tileAnimationSteps[mainGameBoardPositon % 20]
          : [0, 0];
        activeTileRef.current?.animate({ x, y }, { duration: 0 });
      }
    };

    dispatch(setMiniGameDiceResult(response));

    if (!skipAnimation) {
      await new Promise((resolse) => {
        setTimeout(() => {
          resolse(true);
        }, 1500);
      });
    }

    if (response.movesLeft === 0) {
      const totalReward = response.reward.reduce(
        (prev, current) => prev + Object.values(current)[0],
        0,
      );

      if (boardKind === 'tgem') {
        if (!skipAnimation) {
          setFlyingAnimation('tgem');
          setIsRewardScaled(true);

          setTimeout(() => {
            setTgemBalanceScale(true);
          }, 400);
        }
        dispatch(addToBalance({ tgem: totalReward }));

        if (!skipAnimation) {
          await new Promise((res) => setTimeout(res, 1500));
        } else {
          // When animation is skipped, FlyingImg's completeCallback never
          // fires — apply the same end-state manually so we leave mini-game.
          setFlyingAnimation(null);
          setIsRewardScaled(false);
          setTgemBalanceScale(false);
          dispatch(setActiveBoard('dice'));
        }
        moveTile();
      } else if (boardKind === 'str') {
        if (!skipAnimation) {
          setFlyingAnimation('star');
          setIsRewardScaled(true);
          setTimeout(() => dispatch(setTabBarHighlight('Office')), 1000);
          await new Promise((res) => setTimeout(res, 1500));
        } else {
          // Skip animation: ClaimCashCardAnimation's onAnimationEnd never
          // fires — replicate its end-state to return to main board.
          setFlyingAnimation(null);
          setIsRewardScaled(false);
          dispatch(setActiveBoard('dice'));
          dispatch(setTabBarHighlight(null));
        }
        moveTile();
      } else {
        dispatch(setActiveBoard('dice'));
        moveTile();
      }
    }
  };

  return {
    boardPosition,
    diceCount,
    boardKind,
    rollDiceMutation,
    responseHandler,
    reward,
  };
};

export { useMiniDiceGame };
